// every input/select with data-element="input"  will be iterated through to add to the endpoint url using the name="sort" value="name" for values in makeEndpointUrl()
// e.g `apiurl/?name=value&name2=value2`

// data-behaviour="endpoint-input" on change will take the value and do an api call regardless of other select/inputs values
// data-behaviour="go" on click will take makeEndpointUrl
// data-behaviour="go-to" on click will take the data-endpoint and do an api call  based on that regardless of other select/inputs values
// data-behaviour="clear-form" clears the form and getsResults
// data-behaviour="search" triggers after the 3rd character with a 500ms debounce. it also triggers if its changed to be empty
// data-element="input" on change triggers makeEndpointUrl() and resets the pagination to 1 if it isn't the pagination that was the change trigger

// data-behaviour="trigger-search" on select/input will trigger on change
import { _, debounce, makeGtmCall, debug } from '../utils.js'
import axios from 'axios'
import Loading from '../modules/loading.js'
import errorMessageContainer from '../templates/error.js'
import article from '../templates/article.js'
import download from '../templates/download.js'
import card from '../templates/card.js'
import cardAlt from '../templates/card-alt.js'
import search from '../templates/search.js'
import searchCategory from '../templates/search-category.js'
import LazyLoad from "./lazy-load.js";

export default class Listing {
    constructor(el) {
        this.el = el
        this.form = _('form', 'element').nodeFrom(this.el)
        this.searchInput = _('input-search').nodeFrom(this.el)
        this.defaultErrorMessage =
            'Something has gone quite wrong, please try to refresh your browser or contact us.'
        this.pagination = _('pagination', 'element').nodeFrom(this.el)
        this.paginator = _('pagination-input').nodeFrom(this.el)
        this.previousPageTrigger = _('previous-page').nodeFrom(this.el)
        this.nextPageTrigger = _('next-page').nodeFrom(this.el)
        this.paginatorList = _('pagination-list', 'element').nodeFrom(this.el)
        this.totalPages = _('total-pages', 'element').nodeFrom(this.el)
        this.term = _('term', 'element').nodeFrom(this.el)
        this.searchPanel = _('search-panel', 'element').nodeFrom(this.el)
        this.inputs = _('input', 'element').nodesFrom(this.el)
        this.action = this.el.dataset.action || 'get'
        this.resultsContainer = _('results-container', 'element').nodeFrom(
            this.el,
        )
        this.searchCategories = _('search-categories', 'element').nodeFrom(
            this.el,
        )
        this.endpointInput = _('endpoint-input').nodeFrom(this.el)
        this.noResultsContainer = _('no-results', 'element').nodeFrom(this.el)
        this.goTos = _('go-to').nodesFrom(this.el)
        this.gos = _('go').nodesFrom(this.el)
        this.clearFormTrigger = _('clear-form').nodeFrom(this.el)
        this.urlCheck = this.el.dataset.urlCheck || 'true'
        this.onLoad = this.el.dataset.onload || 'true'

        this.endpointInput.addEventListener('change', this.getResults)
        this.form.addEventListener('submit', this.formSubmit)

        this.goTos.map((goto) => {
            goto.addEventListener('click', this.setEndpointUrl)
        })
        this.gos.map((go) => {
            go.addEventListener('click', this.makeEndpointUrl)
        })
        this.clearFormTrigger.addEventListener('click', this.clearForm)

        if (this.previousPageTrigger) {
            this.previousPageTrigger.addEventListener(
                'click',
                this.goPreviousPage,
            )
        }
        if (this.nextPageTrigger) {
            this.nextPageTrigger.addEventListener('click', this.goNextPage)
        }

        this.inputs.map((input) => {
            input.addEventListener('change', this.makeEndpointUrl)
        })
        if (this.searchInput) {
            this.searchInput.addEventListener(
                'keyup',
                debounce(this.search, 500),
            )
            this.searchInput.addEventListener(
                'input',
                this.catchSearchInputCancel,
            )
        }

        if (this.urlCheck === 'true') {
            this.changeInputsToParams()
        }

        if (this.searchInput) {
            if (this.urlCheck === 'true' && this.searchInput.value.length > 0) {
                this.getResults()
            }
        }
    }

    catchSearchInputCancel = () => {
        if (this.searchInput.value.length === 0) {
            this.makeEndpointUrl()
        } else {
            return false
        }
    }

    changeInputsToParams = () => {
        const url = new URL(window.location)
        const params = new URLSearchParams(url.search)
        let paramsPresent = false

        params.forEach((value, key) => {
            if (value.includes('"') && typeof value === 'string') {
                value = value.replaceAll('"', '')
            }
            if (key === 'null' || key === 'modal' || value === 'null') {
                if (debug) {
                    console.warn('you have a null key/value')
                }
                return
            }

            const regex = /^[a-z0-9-_(),&\s]+$/i
            if (!regex.test(key) || !regex.test(value)) {
                if (debug) {
                    console.warn('you have a non-alphanumeric key/value')
                }
                return
            }

            paramsPresent = true
            const input = this.el.querySelector(`[name="${key}"]`)
            const radioInput = this.el.querySelector(`[value="${value}"]`)

            // small catch for if you land directly on search the HTML doesn't exist yet
            if (input === null) {
                return
            }

            if (input && input.type === 'checkbox') {
                input.checked = value === 'true'
            }

            if (input && input.type === 'radio') {
                radioInput.checked = 'true'
            }

            if (
                (input && input.type === 'text') ||
                input.type === 'number' ||
                input.type === 'select-one' ||
                input.type === 'search' ||
                input.type === 'hidden'
            ) {
                input.value = value
            }
        })

        if (paramsPresent) {
            return this.makeEndpointUrl()
        }

        if (!paramsPresent && this.onLoad === 'true') {
            this.makeEndpointUrl()
        }
    }

    formSubmit = (e) => {
        e.preventDefault()
        this.makeEndpointUrl()
    }

    showLoading = () => {
        this.noResultsContainer.classList.add('u-hidden')
        this.resultsContainer.innerHTML =
            '<div class="loading" data-behaviour="loading"></div>'
        _('loading')
            .nodes()
            .map((el) => new Loading(el))
    }

    hideLoading = () => {
        this.resultsContainer.innerHTML = ''
    }

    search = (e) => {
        if (e.target.value.length > 2 || e.target.value.length === 0) {
            this.paginator.value = 1
            this.makeEndpointUrl()
        }
    }

    clearForm = () => {
        if (this.el.dataset.type !== 'search') {
            this.form.reset()
        }
        const tab = _('toggle-select').nodeFrom(this.form)
        const selects = [...this.form.querySelectorAll('select')]
        const radioCategory = this.form.querySelector(
            '[name="category"][value="All"]',
        )
        if (tab) {
            _('toggle-select').node().checked = true
        }
        if (selects) {
            selects.map((select) => {
                select.selectedIndex = 0
            })
        }
        if (radioCategory) {
            radioCategory.checked = true
        }

        this.makeEndpointUrl()
    }

    setEndpointUrl = (e) => {
        this.endpointInput.value = e.currentTarget.dataset.endpoint
        this.getResults()
    }

    resetPaginator = () => {
        if (this.paginator) {
            this.paginator.value = 1
        }
    }

    makeEndpointUrl = (e) => {
        if (
            e &&
            e.currentTarget &&
            e.currentTarget.dataset.behaviour !== 'pagination'
        ) {
            this.resetPaginator()
        }

        // don't trigger on search change
        if (
            e &&
            e.currentTarget.dataset &&
            e.currentTarget.dataset.behaviour === 'input-search' &&
            this.searchInput.value.length !== 0
        ) {
            return false
        }

        this.startEndpoint = this.endpointInput.value.split('?')
        this.endpoint = this.startEndpoint[0]

        if (this.term?.innerHTML !== '') {
            const inputs = _('input', 'element').nodesFrom(this.el)
            if (inputs.length) {
                inputs.map((el, i) => {
                    if (!i) {
                        this.endpoint += '?'
                    }
                    if (el.type !== 'radio' && el.type !== 'checkbox') {
                        this.endpoint += `&${el.getAttribute('name')}=${
                            el.value
                        }`
                    }
                    if (el.type === 'radio' && el.checked) {
                        this.endpoint += `&${el.getAttribute('name')}=${
                            el.value
                        }`
                    }
                    if (el.type === 'checkbox') {
                        this.endpoint += `&${el.getAttribute('name')}=${
                            el.checked
                        }`
                    }
                })
            }
        } else {
            this.endpoint += decodeURIComponent(window.location.search)
        }

        if (this.pagination && !this.paginator.value) {
            this.endpoint += `&${this.paginator.getAttribute('name')}=1`
        }
        this.endpointInput.value = encodeURI(this.endpoint)

        if (
            !!e &&
            !!e.currentTarget &&
            e.currentTarget.dataset.behaviour === 'pagination'
        ) {
            this.getResults(true)
        } else {
            this.resetPaginator()
            this.getResults(false)
        }
    }

    getResults = (scrollTop = false) => {
        if (
            this.el.dataset.type === 'search' &&
            this.searchInput.value.length < 2
        ) {
            return false
        }
        this.showLoading()
        axios({
            method: this.action,
            url: this.endpointInput.value,
        })
            .then((response) => {
                if (this.pagination) {
                    this.buildPagination(response.data)
                }
                if (response.status !== 200) {
                    return this.handleError(response)
                }

                if (!response.data.data.length) {
                    if (this.el.dataset.type === 'search') {
                        this.makeSearchCategories(response.data.categories)
                        return this.handleNoResults(
                            response.data.categories[0].results,
                        )
                    }
                    return this.handleNoResults()
                }
                this.changeParams()
                this.handleSuccess(response.data)
                if (scrollTop) {
                    this.scrollToResultsTop()
                }
            })
            .catch((error) => {
                this.handleError(error)
            })
    }

    changeParams = () => {
        if (this.urlCheck === 'true') {
            const url = new URL(window.location)
            const params = new URLSearchParams(url.search)

            let modalParam = ''
            if (params.has('modal')) {
                modalParam = `&modal=${params.get('modal')}`
            }

            window.history.replaceState(
                null,
                null,
                `${window.location.pathname}?${
                    this.endpointInput.value.split('?')[1]
                }${modalParam}${window.location.hash}`,
            )
        }
    }

    scrollToResultsTop = () => {
        window.scrollTo({
            top:
                this.resultsContainer.getBoundingClientRect().top -
                document.body.getBoundingClientRect().top -
                30,
            behavior: 'smooth',
        })
    }

    goNextPage = () => {
        this.paginator.value++
        this.makeEndpointUrl()
    }

    goPreviousPage = () => {
        this.paginator.value--
        this.makeEndpointUrl()
    }

    changePaginationInput = (event) => {
        this.paginator.value = event.target.value
        this.makeEndpointUrl()
    }

    buildPagination = (response) => {
        if (!response) {
            return false
        }
        if (
            !response.data.length ||
            (response.pagination &&
                response.pagination.totalPages !== null &&
                response.pagination.totalPages < 2)
        ) {
            this.pagination.classList.add('u-hidden')
            return false
        } else {
            this.pagination.classList.remove('u-hidden')
        }
        const totalPages =
            response.pagination?.totalPages ?? response.total_pages
        const currentPage = response.pagination?.currentPage ?? response.page
        let paginationHTML = ''

        const isInRange = (i) => Math.abs(currentPage - i) <= 2

        // Display one empty button before the range if page 1 is not in range
        if (currentPage > 3) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link" value="1" data-behaviour="pagination-button">
            1
        </button>
    </li>`
        }

        // Display one empty button before the range
        if (currentPage > 3) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link pagination__link--no-border" disabled>
            ...
        </button>
    </li>`
        }

        // Display buttons within the range
        for (let i = 1; i <= totalPages; i++) {
            const isCurrentPage = i === currentPage

            if (isCurrentPage || isInRange(i)) {
                paginationHTML += `<li class="pagination__item ${
                    isCurrentPage ? 'pagination__item--active' : ''
                }">
            <button class="pagination__link" value="${i}" data-behaviour="pagination-button">
                ${i}
                ${isCurrentPage ? '<span class="sr-only">current</span>' : ''}
            </button>
        </li>`
            }
        }

        // Display one empty button after the range
        if (currentPage < totalPages - 2) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link pagination__link--no-border" disabled>
            ...
        </button>
    </li>`
        }

        // Display one empty button after the range if the last page is not in range
        if (currentPage < totalPages - 2) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link" value="${totalPages}" data-behaviour="pagination-button">
            ${totalPages}
        </button>
    </li>`
        }

        this.paginatorList.innerHTML = paginationHTML
        this.totalPages.value = response.pagination.totalPages
        this.paginator.value = response.pagination.currentPage

        if (this.previousPageTrigger) {
            this.previousPageTrigger.disabled = currentPage === 1
        }
        if (this.nextPageTrigger) {
            this.nextPageTrigger.disabled = currentPage === totalPages
        }

        _('pagination-button')
            .nodesFrom(this.el)
            .forEach((buttonElement) => {
                buttonElement.addEventListener('click', (event) => {
                    this.changePaginationInput(event)
                })
            })
    }

    handleSuccess = (response) => {
        this.hideLoading()
        this.toggleSearchPanel(true)

        if (this.el.dataset.type === 'article') {
            return this.handleSuccessTypeArticle(response)
        }
        if (this.el.dataset.type === 'download') {
            return this.handleSuccessTypeLegal(response)
        }
        if (this.el.dataset.type === 'card') {
            return this.handleSuccessTypeCard(response, card)
        }
        if (this.el.dataset.type === 'card-alt') {
            return this.handleSuccessTypeCard(response, cardAlt)
        }
        if (this.el.dataset.type === 'search') {
            this.makeSearchCategories(response.categories)
            return this.handleSuccessTypeSearch(response)
        }
        return this.handleError('missing data type')
    }

    toggleSearchPanel = (active = false) => {
        if (this.searchPanel) {
            if (active) {
                this.term.innerHTML = this.searchInput.value
                this.searchPanel.classList.remove('u-hidden')
            } else {
                this.term.innerHTML = ''
                this.searchPanel.classList.add('u-hidden')
            }
        }
    }

    handleSuccessTypeSearch = (response) => {
        let resultsHTML = `<p class="u-mb-30">Displaying result${
            response.pagination.totalResults > 1 ? 's' : ''
        } ${response.pagination.itemFirst} - ${
            response.pagination.itemLast
        } of ${response.pagination.totalResults}</p>`
        if (response.data) {
            response.data.map((el, i) => {
                const item = search(response.data[i])
                resultsHTML += item
            })
        }
        this.resultsContainer.innerHTML = resultsHTML
        this.reEnableModules()
    }

    makeSearchCategories = (categories) => {
        const url = new URL(window.location.origin + this.endpointInput.value)
        const params = new URLSearchParams(url.search)
        const selectedCategory = params.get('category') || 'All'

        let resultsHTML = ''
        if (categories) {
            categories.map((el, i) => {
                const item = searchCategory(i, categories[i], selectedCategory)
                resultsHTML += item
            })
        }
        this.searchCategories.innerHTML = resultsHTML

        const inputs = _('input', 'element').nodesFrom(this.searchCategories)

        inputs.map((input) => {
            input.addEventListener('change', this.makeEndpointUrl)
        })
    }

    handleSuccessTypeLegal = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--equal-height grid--break-2@large grid--break@medium">'
            response.data.map((el, i) => {
                const item = download(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        this.resultsContainer.innerHTML = resultsHTML
        this.reEnableModules()
    }

    handleSuccessTypeCard = (response, template) => {
        let resultsHTML = ''

        if (response.featured) {
            resultsHTML +=
                '<div class="grid grid--3-col grid--equal-height grid--break@medium u-mb">'
            response.featured.map((el, i) => {
                const item = template(response.featured[i], 'card--big u-mb')
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--equal-height grid--break-2@large grid--break@medium">'
            response.data.map((el, i) => {
                const item = template(response.data[i], 'card--small u-mb')
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        this.resultsContainer.innerHTML = resultsHTML
        this.reEnableModules()
    }

    handleSuccessTypeArticle = (response) => {
        let resultsHTML = ''
        if (response.featured) {
            resultsHTML +=
                '<div class="grid grid--3-col grid--equal-height grid--break@medium u-mb0">'
            response.featured.map((el, i) => {
                const item = article(i, response.featured[i], 'article--large')
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--equal-height grid--break-2@large grid--break@small">'
            response.data.map((el, i) => {
                const item = article(i, response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        this.resultsContainer.innerHTML = resultsHTML
        _('image', 'lazyload').nodes().map((el) => new LazyLoad(el))
    }

    reEnableModules = () => {
        _('gtm-click')
            .nodesFrom(this.resultsContainer)
            .map((el) => {
                el.addEventListener('click', () => {
                    makeGtmCall(el)
                })
            })
    }

    handleNoResults = (allCategoriesNo) => {
        if (allCategoriesNo === 0) {
            this.clearFormTrigger.classList.add('u-hidden')
        } else {
            this.clearFormTrigger.classList.remove('u-hidden')
        }
        this.toggleSearchPanel(true)
        this.noResultsContainer.classList.remove('u-hidden')
        this.hideLoading()
    }

    handleError = (err, defaultMessage = this.defaultErrorMessage) => {
        console.warn(err)
        this.hideLoading()
        this.toggleSearchPanel(false)
        let errorHTML = `<p>${defaultMessage}</p>`
        if (err && err.errors) {
            err.errors.map((el) => {
                errorHTML += `<p>${el}</p>`
            })
        }
        this.resultsContainer.innerHTML = errorMessageContainer(errorHTML)
    }
}
